import { useState, useEffect, useCallback, useRef, ReactElement } from "react";
import { Tooltip, Typography } from "@mui/material";

interface TruncatedTextTooltipProps {
  text: string;
}

// Stagger initial truncation checks to avoid 25+ layout reads in one frame (scheduler violations)
let mountStaggerIndex = 0;

/**
 * Component to show tooltip only when text is truncated
 */
const TruncatedTextTooltip = ({ text }: TruncatedTextTooltipProps): ReactElement => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const resizeRafRef = useRef<number>(0);
  const observedNodeRef = useRef<HTMLDivElement | null>(null);

  const checkTruncation = useCallback(() => {
    if (textRef.current && text) {
      const element = textRef.current;
      const truncated = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
      setIsTruncated(truncated);
    } else {
      setIsTruncated(false);
    }
  }, [text]);

  useEffect(() => {
    // Defer initial check and stagger across instances to avoid blocking the main thread
    const order = mountStaggerIndex++;
    const delay = Math.min(order * 20, 400);
    const timeoutId = setTimeout(checkTruncation, delay);

    let resizeObserver: ResizeObserver | null = null;
    const node = textRef.current;
    if (node) {
      observedNodeRef.current = node;
      resizeObserver = new ResizeObserver(() => {
        if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = requestAnimationFrame(() => {
          resizeRafRef.current = 0;
          checkTruncation();
        });
      });
      resizeObserver.observe(node);
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      const observed = observedNodeRef.current;
      if (resizeObserver && observed) {
        resizeObserver.unobserve(observed);
        observedNodeRef.current = null;
      }
      mountStaggerIndex = Math.max(0, mountStaggerIndex - 1);
    };
  }, [checkTruncation]);
  
  const content = (
    <Typography 
      ref={textRef}
      variant="body2" 
      fontWeight="medium" 
      sx={{ 
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: 1.4,
        cursor: isTruncated ? "default" : "inherit",
        wordBreak: "break-word"
      }}
    >
      {text}
    </Typography>
  );
  
  // Only show tooltip if text is actually truncated
  if (isTruncated && text) {
    return (
      <Tooltip 
        title={text}
        arrow
        placement="top"
      >
        {content}
      </Tooltip>
    );
  }
  
  return content;
};

export default TruncatedTextTooltip;
